import React, { useEffect, useMemo, useState } from "react";
import Loader from "../../Snippets/Loader";
import Table from "../../Snippets/TableCustomHooks/Table";
import EmptyDataAlert from "../../Snippets/TableCustomHooks/EmptyDataAlert";
import Cookies from "universal-cookie";
import axios from "axios";
import toast from "react-hot-toast";

const PaymentTable = () => {
  const cookies = new Cookies();
  const token = cookies.get("authToken");

  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [hasData, setHasData] = useState(true);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // 🔥 Detect Case Search
  const isCaseSearch =
    searchValue &&
    !["COMPLETED", "FAILED", "PENDING"].includes(searchValue.toUpperCase());

  // ================= API =================
  const fetchPaymentHistory = async (search = "", currentPage = 1) => {
    try {
      setIsLoading(true);

      let params = {
        page: currentPage,
        limit: limit,
      };

      if (search) {
        const upper = search.toUpperCase();

        if (["COMPLETED", "FAILED", "PENDING"].includes(upper)) {
          params.status = upper;
        } else {
          params.cd_Case_ID = search.trim();
        }
      }

      const res = await axios.get(
        "https://clevdoc-legal-cms-b25ce73f594e.herokuapp.com/payment/payment-history",
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            "module-id": "payment",
          },
        },
      );

      const result = res.data?.data || [];

      setUserData(result);
      setTotalPages(res.data?.totalPages || 1);
      setHasData(result.length > 0);
      setPage(currentPage);
    } catch (error) {
      console.error("API error:", error);
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  };

  const exportExcel = async (caseId) => {
    try {
      setIsExporting(true); // 🔥 start

      const res = await axios.get(
        "https://clevdoc-legal-cms-b25ce73f594e.herokuapp.com/payment/payment-export-excel",
        {
          params: caseId ? { cd_Case_ID: caseId } : {},
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
            "module-id": "PAYMENT",
          },
        },
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        caseId ? `payment_${caseId}.xlsx` : "all_payments.xlsx",
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Excel downloaded successfully ✅"); // 🔥 toast
    } catch (err) {
      console.error(err);
      toast.error("Export failed ❌"); // 🔥 toast
    } finally {
      setIsExporting(false); // 🔥 stop
    }
  };

  // ================= INITIAL LOAD =================
  useEffect(() => {
    if (token) {
      fetchPaymentHistory("", 1);
    }
  }, [token]);

  // ================= SEARCH (Debounce) =================
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchPaymentHistory(searchValue, 1);
    }, 400);

    return () => clearTimeout(delay);
  }, [searchValue]);

  // ================= PAGINATION =================
  const goToNextPage = () => {
    if (page < totalPages) {
      fetchPaymentHistory(searchValue, page + 1);
    }
  };

  const goToPrevPage = () => {
    if (page > 1) {
      fetchPaymentHistory(searchValue, page - 1);
    }
  };

  // ================= TABLE COLUMNS =================
  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "S.No",
        id: "sno",
        Cell: ({ row }) => (page - 1) * limit + row.index + 1,
      },
      {
        Header: "Date",
        accessor: "created_at",
        Cell: ({ value }) => new Date(value).toLocaleString("en-IN"),
      },
      {
        Header: "Transaction ID",
        accessor: "merchantTransactionId",
      },
      {
        Header: "Order ID",
        accessor: "orderId",
      },
      {
        Header: "Case ID",
        accessor: "cd_Case_ID",
        Cell: ({ value }) => value || "—",
      },
      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ value }) => `₹ ${value}`,
      },
      {
        Header: "Payment Mode",
        accessor: "paymentMode",
      },
      {
        Header: "Status",
        accessor: "state",
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 text-xs rounded ${
              value === "COMPLETED"
                ? "bg-green-100 text-green-700"
                : value === "PENDING"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-red-100 text-red-700"
            }`}
          >
            {value}
          </span>
        ),
      },
    ];

    // 🔥 Licence columns (ONLY for case search)
    const licenceColumns = isCaseSearch
      ? [
          {
            Header: "Purchase Date",
            id: "purchaseDate",
            Cell: ({ row }) => {
              const invoice = row.original.invoices?.[0];
              return invoice?.Licence_purchaseDate
                ? new Date(invoice.Licence_purchaseDate).toLocaleDateString(
                    "en-IN",
                  )
                : "—";
            },
          },
          {
            Header: "Expiry Date",
            id: "expiryDate",
            Cell: ({ row }) => {
              const invoice = row.original.invoices?.[0];
              return invoice?.Licence_expiryDate
                ? new Date(invoice.Licence_expiryDate).toLocaleDateString(
                    "en-IN",
                  )
                : "—";
            },
          },
          {
            Header: "Licence Status",
            id: "licenceStatus",
            Cell: ({ row }) => {
              const invoice = row.original.invoices?.[0];
              const status = invoice?.Licence_status;

              if (!status) return "—";

              return (
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    status === "Active"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {status}
                </span>
              );
            },
          },
        ]
      : [];

    // 🔥 Invoice column (always)
    const invoiceColumn = [
      {
        Header: "Invoice",
        id: "invoice",
        Cell: ({ row }) => {
          const invoice = row.original.invoices?.[0];

          return invoice?.url ? (
            <a
              href={invoice.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              View
            </a>
          ) : (
            "—"
          );
        },
      },
    ];

    return [...baseColumns, ...licenceColumns, ...invoiceColumn];
  }, [searchValue]);

  const data = useMemo(() => userData, [userData]);

  return (
    <div className="ml-0 mt-28 md:mt-28 md:ml-64 bg-gray-50 px-4 md:px-10 py-6">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-3">
        <h1 className="text-2xl font-semibold">Payment History</h1>

        <div className="flex gap-2 w-full md:w-auto">
          <input
            type="text"
            placeholder="Search Case ID or Status"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
            className="border px-4 py-2 rounded w-full md:w-80"
          />

          {/* ✅ EXPORT BUTTON */}
          <button
            onClick={() => exportExcel(searchValue)}
            disabled={isExporting}
            className="bg-green-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            {isExporting
              ? "Exporting..."
              : searchValue
                ? "Export"
                : "Export to Excel"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow p-4 h-[70vh] flex flex-col">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Loader />
          </div>
        ) : hasData ? (
          <>
            <div className="flex-1 overflow-x-auto">
              <Table columns={columns} data={data} />
            </div>

            {/* PAGINATION */}
            <div className="flex justify-between mt-4">
              <button
                onClick={goToPrevPage}
                disabled={page === 1}
                className="px-3 py-1 bg-gray-200 rounded"
              >
                Prev
              </button>

              <span>
                Page {page} of {totalPages}
              </span>

              <button
                onClick={goToNextPage}
                disabled={page === totalPages}
                className="px-3 py-1 bg-blue-600 text-white rounded"
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <EmptyDataAlert title="No Payment Data Found" />
        )}
      </div>
    </div>
  );
};

export default PaymentTable;
